import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@client/components/hooks/use-toast';
import { isValidEmailFormat } from '@shared/isValidEmailFormat';
import {
  selectSessionAdvisor,
  selectSubscriptionSeats,
  selectSubscription,
  selectIsSubscriptionOwner,
  selectFetchStatus,
  selectMutationStatus,
  selectInvitationPreviewStatus,
  selectInvitationPreview,
  selectCreditBalance,
  selectUnitPricePerSeat,
  selectSubscriptionOwnerInfo,
  selectCurrentAdvisorAssignedDateTime,
} from '@client/model/selectors';
import {
  fetchSubscriptionSeatsByAdvisorIdRequest,
  postSubscriptionSeatByAdvisorIdRequest,
  deleteSubscriptionSeatByAdvisorIdRequest,
  postSeatInvitationByAdvisorIdRequest,
  postDirectAssignSeatByAdvisorIdRequest,
  clearInvitationPreview,
} from '@client/model/subscriptionSeatsModel';

export type DialogType = 'addSeat' | 'removeSeat' | null;

/**
 * Hook for managing subscription seats
 * @param targetAdvisorId - Optional advisor ID for admin mode. If provided, manages that advisor's subscription.
 *                          If not provided, uses the session advisor (normal mode).
 */
export function useSubscriptionSeats(targetAdvisorId?: string | null) {
  const dispatch = useDispatch();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const seats = useSelector(selectSubscriptionSeats);
  const subscription = useSelector(selectSubscription);
  const isOwner = useSelector(selectIsSubscriptionOwner);
  const { isFetching, error: fetchError } = useSelector(selectFetchStatus);
  const { isMutating, error: mutationError, currentMutation } = useSelector(selectMutationStatus);
  const { status: invitationPreviewStatus, isPreviewingInvitation, error: invitationPreviewError } = useSelector(selectInvitationPreviewStatus);
  const invitationPreview = useSelector(selectInvitationPreview);
  const creditBalance = useSelector(selectCreditBalance);
  const unitPricePerSeat = useSelector(selectUnitPricePerSeat);
  const ownerInfo = useSelector(selectSubscriptionOwnerInfo);
  const currentAdvisorAssignedDateTime = useSelector(selectCurrentAdvisorAssignedDateTime);

  const [dialogType, setDialogType] = useState<DialogType>(null);
  const [selectedSeatId, setSelectedSeatId] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [duplicateEmailError, setDuplicateEmailError] = useState<string | null>(null);

  const { toast } = useToast();
  const prevMutatingRef = useRef(false);
  const prevMutationRef = useRef<string | null>(null);

  // Use targetAdvisorId if provided (admin mode), otherwise use session advisor
  const advisorId = targetAdvisorId ?? sessionAdvisor?.advisorId;

  useEffect(() => {
    if (advisorId) {
      dispatch(fetchSubscriptionSeatsByAdvisorIdRequest(advisorId));
    }
  }, [advisorId, dispatch]);

  // Show toast when mutations complete
  useEffect(() => {
    // Mutation just completed (was mutating, now not)
    if (prevMutatingRef.current && !isMutating) {
      const mutationType = prevMutationRef.current;

      if (mutationError) {
        toast({
          title: 'Error',
          description: mutationError,
          variant: 'destructive',
          className: '-translate-y-5'
        });
      } else if (mutationType) {
        const messages: Record<string, { title: string; description: string }> = {
          add: { title: 'Seat Added', description: 'A new seat has been added to your subscription.' },
          remove: { title: 'Seat Removed', description: 'The seat has been removed from your subscription.' },
          invite: { title: 'Invitation Sent', description: 'The invitation has been sent successfully.' },
          direct_assign: { title: 'Seat Assigned', description: 'The seat has been assigned directly.' },
        };

        const message = messages[mutationType];
        if (message) {
          toast({
            title: message.title,
            description: message.description,
            className: ' -translate-y-5',
          });
        }
      }
    }

    prevMutatingRef.current = isMutating;
    prevMutationRef.current = currentMutation;
  }, [isMutating, mutationError, currentMutation, toast]);

  const handlePreviewInvitation = () => {
    if (!emailInput || !advisorId) return;

    const normalizedEmail = emailInput.toLowerCase().trim();

    // Check if email format is valid
    if (!isValidEmailFormat(normalizedEmail)) {
      setDuplicateEmailError('Please enter a valid email address.');
      return;
    }

    // Check if email already exists in the active seats
    const existingSeat = seats.filter(seat => seat.status === 'active' || seat.status === 'invited').find(seat =>
      seat.advisorEmail?.toLowerCase().trim() === normalizedEmail ||
      seat.invitedEmail?.toLowerCase().trim() === normalizedEmail
    );

    if (existingSeat) {
      setDuplicateEmailError('This email is already associated with a seat in this subscription.');
      return;
    }

    // Clear any previous duplicate email error
    setDuplicateEmailError(null);

    dispatch(
      postSeatInvitationByAdvisorIdRequest({
        advisorId,
        advisorEmail: emailInput,
        isPreview: true,
      })
    );
    setShowPreview(true);
  };

  const handleSendInvitation = () => {
    if (!emailInput || !advisorId) return;
    dispatch(
      postSeatInvitationByAdvisorIdRequest({
        advisorId,
        advisorEmail: emailInput.toLowerCase().trim(),
        isPreview: false,
      })
    );
    setDialogType(null);
    setEmailInput('');
    setShowPreview(false);
    dispatch(clearInvitationPreview());
  };

  const handleRemoveSeat = () => {
    if (!selectedSeatId || !advisorId) return;
    dispatch(
      deleteSubscriptionSeatByAdvisorIdRequest({
        advisorId,
        subscriptionSeatId: selectedSeatId,
      })
    );
    setDialogType(null);
    setSelectedSeatId(null);
  };

  const handleResendInvitation = (seatId: string, email: string) => {
    if (!advisorId) return;
    dispatch(
      postSeatInvitationByAdvisorIdRequest({
        advisorId,
        subscriptionSeatId: seatId,
        advisorEmail: email,
        isPreview: false,
      })
    );
  };

  const handleDirectAssignSeat = () => {
    if (!advisorId) return;

    // Prefer advisorId from preview if available
    const targetAdvisorId = invitationPreview?.advisorInfo?.advisorId;

    dispatch(
      postDirectAssignSeatByAdvisorIdRequest({
        advisorId,
        targetAdvisorId,
        advisorEmail: targetAdvisorId ? undefined : emailInput,
      })
    );
    setDialogType(null);
    setEmailInput('');
    setShowPreview(false);
    dispatch(clearInvitationPreview());
  };

  const openDialog = (type: DialogType, seatId?: string) => {
    setDialogType(type);
    if (seatId) setSelectedSeatId(seatId);
  };

  const closeDialog = () => {
    setDialogType(null);
    setEmailInput('');
    setSelectedSeatId(null);
    setShowPreview(false);
    setDuplicateEmailError(null);
    dispatch(clearInvitationPreview());
  };

  const activeSeatsCount = seats.filter(seat => seat.status === 'active').length;

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);
    // Clear duplicate email error when user types
    if (duplicateEmailError) {
      setDuplicateEmailError(null);
    }
  };

  return {
    // Data
    targetAdvisorId: advisorId,
    seats,
    subscription,
    isOwner,
    creditBalance,
    unitPricePerSeat,
    activeSeatsCount,
    ownerInfo,
    currentAdvisorAssignedDateTime,

    // Fetch status
    isFetching,
    fetchError,

    // Mutation status
    isMutating,
    mutationError,
    currentMutation,

    // Invitation preview
    invitationPreviewStatus,
    isPreviewingInvitation,
    invitationPreviewError,
    invitationPreview,
    duplicateEmailError,

    // Dialog state
    dialogType,
    selectedSeatId,
    emailInput,
    setEmailInput: handleEmailInputChange,
    showPreview,
    setShowPreview,

    // Actions
    openDialog,
    closeDialog,
    handlePreviewInvitation,
    handleSendInvitation,
    handleRemoveSeat,
    handleResendInvitation,
    handleDirectAssignSeat,
  };
}

export type UseSubscriptionSeatsReturn = ReturnType<typeof useSubscriptionSeats>;
